import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { translate } from '@jsverse/transloco';
import { debounceTime, Subject, takeUntil } from 'rxjs';

import { AppSharedModule } from 'app/app-shared.module';
import { Scale } from 'app/components/uikit/lex-horizontal-stepper/lex-horizontal-stepper.component';
import { Comparison } from 'app/providers/bridge-api/dashboard.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { Utils } from 'app/utils/utils';
import { decimalsToPipeDigitInfo, createStepperScale } from '../widget-predictive/predictive.utils';
import { WidgetPredictiveOptions } from '../widget-predictive/widget-predictive.component';
import { Driver } from '../../models/predictive.models';
import { SmartSummaryService } from '../../services/smart-summary.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-driver-table',
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './driver-table.component.html',
  styleUrl: './driver-table.component.scss',
})
export class DriverTableComponent implements OnInit, AfterViewInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() drivers!: Driver[];
  @Input() predictiveOptions!: WidgetPredictiveOptions;
  @Input() isReadOnly!: boolean;
  @Input() isDashboardLocked!: boolean;
  @Input() comparison?: Comparison;

  @Output() simulate = new EventEmitter<Driver>();
  @Output() reset = new EventEmitter<Driver>();
  @Output() smartSummary = new EventEmitter<Driver>();
  @Output() hover = new EventEmitter<Driver | null>();

  @ViewChild(MatSort) sort: MatSort;

  DISPLAYED_COLUMNS = ['importanceCounter', 'driver', 'trend', 'score', 'simulated', 'actions'];

  readonly TOP_COLUMNS = ['importanceCounter-top', 'driver-top', 'score-top', 'actions-top'];

  dataSource: MatTableDataSource<Driver>;
  numberPipeDigitInfo: string;
  scale: Scale;
  scoringKey: string;
  hasSmartSummaryFeature = false;

  private readonly smartSummaryService = inject(SmartSummaryService);

  private readonly hover$ = new Subject<Driver | null>();
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.init();

    this.createSubscriptions();
  }

  ngOnChanges(): void {
    if (this.hasDataSourceChanged()) {
      this.init();
    }
  }

  ngAfterViewInit(): void {
    this.initSorting();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  hasDataSourceChanged(): boolean {
    return this.dataSource?.data && !Utils.deepEquals(this.drivers, this.dataSource.data);
  }

  initSorting(): void {
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  onReset(driver: Driver): void {
    driver.simulated = null;

    this.reset.emit(driver);
  }

  onSmartSummary(driver: Driver): void {
    this.smartSummary.emit(driver);
  }

  onHover(driver: Driver | null): void {
    this.hover$.next(driver);
  }

  onSimulatedValueChange(simulated: number, driver: Driver): void {
    driver.simulated = simulated;

    this.simulate.emit(driver);
  }

  private async init(): Promise<void> {
    if (!this.shouldShowTrend) {
      this.DISPLAYED_COLUMNS = this.DISPLAYED_COLUMNS.filter((c) => c !== 'trend');
    }

    this.dataSource = new MatTableDataSource(this.drivers);
    this.initSorting();

    this.numberPipeDigitInfo = decimalsToPipeDigitInfo(this.predictiveOptions.decimals);

    this.scale = createStepperScale(
      this.predictiveOptions.driverSettings,
      this.predictiveOptions.stepValue
    );

    this.scoringKey = this.predictiveOptions.driverSettings.scoring.toLowerCase();

    this.hasSmartSummaryFeature =
      await this.smartSummaryService.hasPermissionForSummaryByDriverField();
  }

  private createSubscriptions(): void {
    this.hover$
      .pipe(takeUntil(this.destroy$), debounceTime(1))
      .subscribe((d) => this.hover.emit(d));
  }

  private get shouldShowTrend(): boolean {
    return this.comparison?.filters.length > 1;
  }

  get trendHeader(): string {
    return this.comparison?.filters[1].label;
  }

  get scoreHeader(): string {
    return this.shouldShowTrend
      ? this.comparison.filters[0].label
      : translate('driver-table.score');
  }

  get isDisabled(): boolean {
    return this.isReadOnly || this.isDashboardLocked;
  }
}
